import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

export interface ActivityItem {
  id: string;
  type: 'pr_created' | 'pr_merged' | 'pr_closed' | 'task_picked' | 'pr_review';
  timestamp: string;
  projectId: string;
  projectName: string;
  repositoryId: string;
  repositoryName: string;
  description: string;
  externalUrl: string;
  metadata?: {
    prNumber?: number;
    taskId?: string;
    branchName?: string;
    status?: string;
  };
}

interface ActivityState {
  activities: ActivityItem[];
  total: number;
  hasMore: boolean;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: ActivityState = {
  activities: [],
  total: 0,
  hasMore: false,
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getActivityFeed = createAsyncThunk(
  'activity/getActivityFeed',
  async (
    { organizationId, projectId, limit = 50, offset = 0 }: { organizationId: string; projectId?: string; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('organizationId', organizationId);
      if (projectId) {
        params.append('projectId', projectId);
      }
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await apiRequest(`/activity?${params.toString()}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| SLICE ||============================== //

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get activity feed
      .addCase(getActivityFeed.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getActivityFeed.fulfilled, (state, action) => {
        if (!action.payload) {
          state.activities = [];
          state.total = 0;
          state.hasMore = false;
          state.status = 'success';
          state.error = '';
          return;
        }

        const data = action.payload.response || action.payload;
        state.activities = data.activities || [];
        state.total = data.total || 0;
        state.hasMore = data.hasMore || false;
        state.status = 'success';
        state.error = '';
      })
      .addCase(getActivityFeed.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        state.activities = [];
      });
  },
});

// ==============================|| SELECTORS ||============================== //

export const selectActivities = (state: { activity: ActivityState }) => state.activity.activities;
export const selectActivityTotal = (state: { activity: ActivityState }) => state.activity.total;
export const selectActivityHasMore = (state: { activity: ActivityState }) => state.activity.hasMore;
export const selectActivityStatus = (state: { activity: ActivityState }) => state.activity.status;
export const selectActivityError = (state: { activity: ActivityState }) => state.activity.error;

export default activitySlice.reducer;
